/* ============================================================================
   File: assets/aussie-style-edit-builder.js
   "Build Your Aussie Style Edit" bundle builder — FIXED PRICE version.

   IMPORTANT — pricing/security:
   The price the customer pays is NEVER computed here. Once the shopper
   completes their N-piece selection, this script adds exactly ONE line
   item to the cart: the dedicated "Bundle" product/variant, whose price
   ($397.95 AUD by default) is set directly on the product in Shopify and
   validated server-side by Shopify's own /cart/add.js Cart AJAX API.

   The individual pieces the shopper picked are attached to that single
   line item as cart line item properties (visible on the order for
   fulfilment) — they are NOT added to the cart as separate line items.

   This design deliberately keeps the bundle price INDEPENDENT from the
   store's other native Automatic Discounts ("Buy 2/3/4/5 -> X%"): the
   bundle product is not a member of any collection those discounts
   target, so they can never combine with or apply on top of the fixed
   bundle price.

   DRAWER: the summary/checkout card lives in a fixed-position drawer
   that opens automatically the instant the shopper picks their last
   piece (no scrolling needed to find the CTA). It can also be reopened
   at ANY time — even mid-selection, before the Edit is complete — via
   the sticky bar's "REVIEW MY EDIT" button, and closed via its own
   close button, the overlay, or the Escape key.

   DRAWER ITEM LIST: while the drawer is open, every currently-selected
   piece is listed with its thumbnail photo, title, and chosen
   colour/size (if any), each with its own remove button — so the
   shopper can see exactly what they've picked so far and take pieces
   back out at any point, without needing to scroll back through the
   whole grid to find the card again. This list re-renders every time a
   piece is added or removed (see renderDrawerItems()).

   REPEATED PIECES: a shopper may add the SAME product to their Edit more
   than once — either the exact same variant again, or a different
   colour/size of it. Selections are therefore tracked as a LIST of
   individual entries (each with its own entryId), not a set keyed by
   product id, so two entries can share the same product. Every entry
   still carries its own variant/colour/size snapshot and its own remove
   control in the drawer, so each piece can be taken back out
   individually regardless of how many times that product was added.

   CHECKOUT: once the bundle line item is successfully added to the
   cart, the shopper is sent straight to Shopify's native checkout
   (not the cart page) — one less click. If they're logged into a
   customer account, Shopify's checkout automatically uses that account
   (pre-filled address/contact info) with no extra code needed here.

   STICKY BAR POSITION: the mobile/desktop sticky bar (selection count +
   "REVIEW MY EDIT" button) is pinned to the TOP of the viewport,
   directly under the store's own sticky header, instead of the bottom.
   Its offset is recalculated live (see updateStickyOffset()) since the
   header itself switches between normal document flow and being pinned
   as the shopper scrolls.

   COLOUR SWATCHES: colour option values render as a compact DROPDOWN —
   a closed trigger (small preview photo + colour name) that expands
   into a small floating panel of photo swatches (see
   snippets/aussie-style-edit-card.liquid). Picking a swatch also swaps
   the card's main photo to that variant's own image via data-image, so
   the shopper sees the exact piece in the colour they're choosing, and
   collapses the dropdown again — keeping cards with many colours (e.g.
   the bag keychain charm) compact instead of showing every swatch at
   once.

   SIZE / OTHER OPTIONS: every other option (Size, etc.) uses the same
   compact dropdown pattern, just with plain text value buttons instead
   of photo swatches.

   CATEGORY FILTER GROUPS: top-level category pills (e.g. "Dresses") in
   the filter bar are plain flat filters — NOT dropdowns. Clicking one
   shows every product from every subcollection grouped under it at once
   (matched via data-aussie-filter-group, a comma-separated list of
   collection handles), so there is no subcollection picker to open.

   SIZE CHART: every card has its own small "Size Chart" text link (see
   snippets/aussie-style-edit-card.liquid). Tapping it fetches that exact
   product's own native Size Chart popup — the SAME feature already used
   on this store's real product pages (snippets/size-chart-drawer.liquid)
   — via Shopify's Section Rendering API (using the hidden
   sections/aussie-edit-sizechart-render.liquid section as the renderer),
   injects it into the single [data-aussie-edit-sizechart-native]
   container, and opens it immediately. Only one product's chart is ever
   injected at a time — a new tap clears whatever was there before. If
   the product has no chart configured, a brief inline note is shown
   instead (see loadSizeChart()).
============================================================================ */
(function () {
  'use strict';

  document.querySelectorAll('[data-aussie-edit]').forEach(initAussieEdit);

  // Only one dropdown (colour swatch or size) should be open at a time,
  // and any open dropdown should collapse on an outside click or Escape —
  // handled once at document level rather than per-card.
  function closeAllDropdowns() {
    document.querySelectorAll('.aussie-edit__dropdown.is-open').forEach(function (dropdown) {
      dropdown.classList.remove('is-open');
      var panel = dropdown.querySelector('[data-aussie-edit-swatch-panel], [data-aussie-edit-value-panel]');
      if (panel) panel.hidden = true;
      var trigger = dropdown.querySelector('[data-aussie-edit-swatch-trigger], [data-aussie-edit-value-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }
  document.addEventListener('click', closeAllDropdowns);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAllDropdowns();
  });

  // Minimal HTML-escaping helper — used when building the drawer's item
  // list via innerHTML, since product titles/option text come from data
  // attributes (already escaped once for HTML-attribute context by
  // Liquid, but decoded back to plain text by the browser) and must be
  // re-escaped before being placed into element content.
  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function initAussieEdit(root) {
    var bundleSize = parseInt(root.dataset.bundleSize, 10) || 4;
    var fixedPrice = parseFloat(root.dataset.fixedPrice) || 0;
    var bundleVariantId = root.dataset.bundleVariantId;
    var pageSize = parseInt(root.dataset.pageSize, 10) || 12;

    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-aussie-edit-card]'));
    // Category group pills use data-aussie-filter-group (a comma-separated
    // list of every collection handle grouped under that category) instead
    // of a single data-aussie-filter value — select both kinds here.
    var filters = root.querySelectorAll('[data-aussie-filter], [data-aussie-filter-group]');
    var progressBar = root.querySelector('[data-aussie-edit-progress]');

    // ---- Infinite scroll ----
    // Each "collection" block's initial page (rendered server-side) is
    // followed by a hidden [data-collection-marker] carrying its handle,
    // total product count, and how many were already rendered. As the
    // shopper scrolls near the grid's bottom sentinel, we fetch further
    // pages of whichever collection(s) match the active filter via
    // Shopify's Section Rendering API — using the hidden
    // sections/aussie-edit-card-batch.liquid section as the renderer, the
    // exact same mechanism this store's normal collection pages use
    // (assets/infinite-scroll.js). This never touches pricing: it only
    // ever appends more read-only product cards, all wired up through the
    // same setupCard()/cards array as the initially-rendered ones.
    var grid = root.querySelector('[data-aussie-edit-grid]');
    var sentinel = root.querySelector('[data-aussie-edit-sentinel]');
    var loadingEl = root.querySelector('[data-aussie-edit-loading]');
    var cardBatchMarker = document.querySelector('[data-aussie-edit-card-batch-marker]');
    var cardBatchSectionId = cardBatchMarker ? cardBatchMarker.dataset.sectionId : null;

    var collectionState = {};
    root.querySelectorAll('[data-collection-marker]').forEach(function (marker) {
      var handle = marker.dataset.handle;
      if (!handle) return;
      var total = parseInt(marker.dataset.total, 10) || 0;
      var rendered = parseInt(marker.dataset.rendered, 10) || 0;
      collectionState[handle] = {
        total: total,
        rendered: rendered,
        nextPage: 2, // page 1 is whatever was already rendered server-side
        loading: false,
        exhausted: rendered >= total
      };
    });

    function activeFilterHandles() {
      var activeBtn = null;
      filters.forEach(function (b) {
        if (b.classList.contains('is-active')) activeBtn = b;
      });
      if (!activeBtn) return null;
      if (activeBtn.dataset.aussieFilterGroup) {
        return activeBtn.dataset.aussieFilterGroup.split(',').filter(Boolean);
      }
      var f = activeBtn.dataset.aussieFilter;
      if (!f || f === 'all') return null; // null = every collection ("All")
      return [f];
    }

    function applyActiveFilterVisibility() {
      var handles = activeFilterHandles();
      cards.forEach(function (card) {
        var collections = (card.dataset.productCollections || '').split(',');
        var matches = !handles || handles.some(function (h) { return collections.indexOf(h) !== -1; });
        card.style.display = matches ? '' : 'none';
      });
    }

    function isSentinelVisible() {
      if (!sentinel) return false;
      var rect = sentinel.getBoundingClientRect();
      return rect.top < window.innerHeight + 800 && rect.bottom > -800;
    }

    function pickNextHandleToLoad() {
      var handles = activeFilterHandles();
      var candidates = handles || Object.keys(collectionState);
      for (var i = 0; i < candidates.length; i++) {
        var state = collectionState[candidates[i]];
        if (state && !state.exhausted && !state.loading) return candidates[i];
      }
      return null;
    }

    function loadMoreForActiveFilter() {
      if (!cardBatchSectionId || !grid) return;
      var handle = pickNextHandleToLoad();
      if (!handle) {
        if (loadingEl) loadingEl.hidden = true;
        return;
      }
      var state = collectionState[handle];
      state.loading = true;
      if (loadingEl) loadingEl.hidden = false;

      var url = '/collections/' + handle + '?page=' + state.nextPage + '&section_id=' + cardBatchSectionId;

      fetch(url, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('Failed to load more products (HTTP ' + res.status + ')');
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var batchGrid = doc.querySelector('[data-aussie-edit-batch-grid]');
          var newCards = batchGrid
            ? Array.prototype.slice.call(batchGrid.querySelectorAll('[data-aussie-edit-card]'))
            : [];

          newCards.forEach(function (newCard) {
            grid.appendChild(newCard);
            setupCard(newCard);
            cards.push(newCard);
          });

          state.rendered += newCards.length;
          state.nextPage += 1;
          state.loading = false;
          if (newCards.length === 0 || state.rendered >= state.total) {
            state.exhausted = true;
          }

          applyActiveFilterVisibility();

          // Keep loading if the sentinel is still visible (this batch
          // didn't fill the viewport) and there's more to fetch —
          // mirrors the theme's own infinite-scroll.js chaining.
          if (isSentinelVisible() && pickNextHandleToLoad()) {
            loadMoreForActiveFilter();
          } else if (loadingEl) {
            loadingEl.hidden = true;
          }
        })
        .catch(function (err) {
          console.error('[Aussie Edit] Could not load more products', err);
          state.loading = false;
          if (loadingEl) loadingEl.hidden = true;
        });
    }

    if (sentinel && 'IntersectionObserver' in window) {
      var infiniteScrollObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) loadMoreForActiveFilter();
          });
        },
        { rootMargin: '800px 0px 800px 0px' }
      );
      infiniteScrollObserver.observe(sentinel);
    }

    var countEl = root.querySelector('[data-aussie-edit-count]');
    var summaryEl = root.querySelector('[data-aussie-edit-summary]');
    var priceEl = root.querySelector('[data-aussie-edit-price]');
    var saveEl = root.querySelector('[data-aussie-edit-save]');
    var retailEl = root.querySelector('[data-aussie-edit-retail]');
    var finalEl = root.querySelector('[data-aussie-edit-final]');
    var savingsEl = root.querySelector('[data-aussie-edit-savings]');
    var statusEl = root.querySelector('[data-aussie-edit-status]');
    var addToCartBtn = root.querySelector('[data-aussie-edit-add-to-cart]');
    var sticky = root.querySelector('[data-aussie-edit-sticky]');
    var stickyCount = root.querySelector('[data-aussie-edit-sticky-count]');
    var stickyCta = root.querySelector('[data-aussie-edit-sticky-cta]');
    var drawer = root.querySelector('[data-aussie-edit-drawer]');
    var drawerOverlay = root.querySelector('[data-aussie-edit-drawer-overlay]');
    var drawerClose = root.querySelector('[data-aussie-edit-drawer-close]');
    var drawerItemsEl = root.querySelector('[data-aussie-edit-drawer-items]');
    var drawerCloseTimer = null;

    // ---- Size Chart (fetched on demand per product) ----
    var sizechartContainer = root.querySelector('[data-aussie-edit-sizechart-native]');
    var sizechartMarker = document.querySelector('[data-aussie-edit-sizechart-marker]');
    var sizechartSectionId = sizechartMarker ? sizechartMarker.dataset.sectionId : null;
    var sizechartLoadToken = 0;

    // <script> tags inserted via innerHTML never execute, so the native
    // Size Chart popup's own inline <script> (which wires up its click
    // handlers) needs to be re-created as a fresh element to actually run.
    function executeInjectedScripts(container) {
      var scripts = Array.prototype.slice.call(container.querySelectorAll('script'));
      scripts.forEach(function (oldScript) {
        var newScript = document.createElement('script');
        Array.prototype.slice.call(oldScript.attributes).forEach(function (attr) {
          newScript.setAttribute(attr.name, attr.value);
        });
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    }

    function clearSizechart() {
      if (sizechartContainer) sizechartContainer.innerHTML = '';
    }

    function loadSizeChart(handle, triggerBtn, statusEl) {
      if (!sizechartSectionId || !sizechartContainer || !handle) return;

      if (statusEl) {
        statusEl.hidden = true;
        statusEl.textContent = '';
      }
      var originalLabel = triggerBtn.textContent;
      triggerBtn.disabled = true;
      triggerBtn.textContent = 'Loading…';

      var token = ++sizechartLoadToken;

      fetch('/products/' + handle + '?section_id=' + sizechartSectionId, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('Failed to load size chart (HTTP ' + res.status + ')');
          return res.text();
        })
        .then(function (html) {
          if (token !== sizechartLoadToken) return; // a newer tap superseded this one
          triggerBtn.disabled = false;
          triggerBtn.textContent = originalLabel;

          var doc = new DOMParser().parseFromString(html, 'text/html');
          if (!doc.getElementById('sc-tab-trigger')) {
            if (statusEl) {
              statusEl.textContent = 'Size chart not available for this item.';
              statusEl.hidden = false;
            }
            return;
          }

          clearSizechart();
          sizechartContainer.innerHTML = html;
          executeInjectedScripts(sizechartContainer);
          var openTrigger = sizechartContainer.querySelector('#sc-tab-trigger');
          if (openTrigger) openTrigger.click();
        })
        .catch(function (err) {
          console.error('[Aussie Edit] Could not load size chart', err);
          if (token !== sizechartLoadToken) return;
          triggerBtn.disabled = false;
          triggerBtn.textContent = originalLabel;
          if (statusEl) {
            statusEl.textContent = 'Could not load size chart — try again.';
            statusEl.hidden = false;
          }
        });
    }

    // ---- Sticky bar top offset ----
    // The mobile/desktop sticky bar is pinned to the TOP of the viewport
    // (directly under the store's own header) instead of the bottom. This
    // theme's header (Broadcast) toggles between sitting in normal
    // document flow at the very top of the page and being pinned as
    // position:fixed once the shopper scrolls, so the space it occupies
    // above our bar isn't constant. This measures the header's current
    // on-screen height (0 if it isn't currently pinned) and updates the
    // bar's "top" offset to sit flush beneath it, re-checked on
    // scroll/resize since the header's pinned state can change.
    var themeHeaderWrapper = document.querySelector('header-component.header__wrapper');
    var themeHeaderSections = document.querySelector('.header-sections');
    var stickyOffsetRaf = null;
    function updateStickyOffset() {
      if (!sticky) return;
      var offset = 0;
      if (themeHeaderWrapper && window.getComputedStyle(themeHeaderWrapper).position === 'fixed') {
        // Header is "stuck" (pinned) — sit right below its own height.
        var headerInner = themeHeaderWrapper.querySelector('.theme__header') || themeHeaderWrapper;
        var rect = headerInner.getBoundingClientRect();
        offset = Math.max(rect.bottom, 0);
      } else if (themeHeaderSections) {
        // Header isn't pinned yet — if the announcement bar + header are
        // still visible in their normal document-flow position at the top
        // of the page (shopper hasn't scrolled past them), sit right below
        // them instead of at true viewport top, so we never sit on top of
        // (or under) the announcement bar's own high z-index.
        var sectionsRect = themeHeaderSections.getBoundingClientRect();
        if (sectionsRect.top <= 0 && sectionsRect.bottom > 0) {
          offset = sectionsRect.bottom;
        }
      }
      sticky.style.top = offset + 'px';
    }
    function scheduleStickyOffsetUpdate() {
      if (stickyOffsetRaf) return;
      stickyOffsetRaf = window.requestAnimationFrame(function () {
        stickyOffsetRaf = null;
        updateStickyOffset();
      });
    }
    updateStickyOffset();
    window.addEventListener('scroll', scheduleStickyOffsetUpdate, { passive: true });
    window.addEventListener('resize', scheduleStickyOffsetUpdate);

    // selections: Array<{ entryId, productId, variantId, compareAt, title,
    // optionText, image }> — a LIST (not a map keyed by product id), so the
    // same product can appear more than once (same variant repeated, or a
    // different colour/size each time). Each entry is independent and can
    // be removed on its own via the drawer's per-item remove button.
    var selections = [];
    var nextEntryId = 1;
    var hasFiredStart = false;
    var hasFiredComplete = false;

    // ---- Drawer open/close ----
    function openDrawer() {
      if (!drawer || !drawerOverlay) return;
      if (drawerCloseTimer) {
        window.clearTimeout(drawerCloseTimer);
        drawerCloseTimer = null;
      }
      drawer.hidden = false;
      drawerOverlay.hidden = false;
      // Force layout so the transition runs from the hidden state.
      // eslint-disable-next-line no-unused-expressions
      drawer.offsetHeight;
      drawer.classList.add('is-open');
      drawerOverlay.classList.add('is-open');
      if (sticky) sticky.classList.add('is-hidden-by-drawer');
      document.documentElement.style.overflow = 'hidden';
    }

    function closeDrawer() {
      if (!drawer || !drawerOverlay) return;
      drawer.classList.remove('is-open');
      drawerOverlay.classList.remove('is-open');
      if (sticky) sticky.classList.remove('is-hidden-by-drawer');
      document.documentElement.style.overflow = '';
      drawerCloseTimer = window.setTimeout(function () {
        drawer.hidden = true;
        drawerOverlay.hidden = true;
      }, 300);
    }

    if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
    if (drawerOverlay) drawerOverlay.addEventListener('click', closeDrawer);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    // Removing a specific piece directly from the drawer's item list —
    // delegated so it keeps working for cards appended later via infinite
    // scroll. Each row is keyed by its own entryId (not product id), so
    // removing one entry never affects any other entry of the same product
    // still in the Edit.
    if (drawerItemsEl) {
      drawerItemsEl.addEventListener('click', function (e) {
        var removeBtn = e.target.closest('[data-aussie-edit-drawer-item-remove]');
        if (!removeBtn) return;
        var itemEl = removeBtn.closest('[data-aussie-edit-drawer-item]');
        var entryId = itemEl ? itemEl.dataset.entryId : null;
        if (!entryId) return;
        removeEntry(entryId);
      });
    }

    // ---- View analytics ----
    if ('IntersectionObserver' in window) {
      var seen = false;
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !seen) {
            seen = true;
            pushEvent('bundle_view', { bundle_name: root.dataset.bundleName || 'Build Your Aussie Style Edit' });
          }
        });
      }, { threshold: 0.25 }).observe(root);
    }

    // ---- Set up each card ----
    cards.forEach(setupCard);

    function setupCard(card) {
      var variantsScript = card.querySelector('[data-aussie-edit-variants]');
      var variants = [];
      try {
        variants = JSON.parse(variantsScript ? variantsScript.textContent : '[]');
      } catch (e) {
        variants = [];
      }

      var cardImage = card.querySelector('[data-aussie-edit-image]');

      var sizechartTrigger = card.querySelector('[data-aussie-edit-sizechart-trigger]');
      var sizechartStatus = card.querySelector('[data-aussie-edit-sizechart-status]');
      if (sizechartTrigger) {
        sizechartTrigger.addEventListener('click', function () {
          loadSizeChart(sizechartTrigger.dataset.productHandle, sizechartTrigger, sizechartStatus);
        });
      }

      var optionGroups = Array.prototype.slice.call(card.querySelectorAll('[data-aussie-edit-option]'));
      var selectedOptions = new Array(optionGroups.length).fill(null);
      var addBtn = card.querySelector('[data-aussie-edit-add]');
      var hint = card.querySelector('[data-aussie-edit-hint]');

      function findVariant() {
        if (optionGroups.length === 0) {
          return variants[0] || null;
        }
        if (selectedOptions.some(function (v) { return v === null; })) return null;
        return variants.find(function (v) {
          return selectedOptions.every(function (val, idx) {
            var key = 'option' + (idx + 1);
            return v[key] === val;
          });
        }) || null;
      }

      function optionSummary() {
        return selectedOptions.filter(Boolean).join(' / ');
      }

      function refreshOptionAvailability() {
        optionGroups.forEach(function (group, idx) {
          var buttons = group.querySelectorAll('[data-aussie-edit-option-value]');
          buttons.forEach(function (btn) {
            var value = btn.dataset.value;
            var otherSelections = selectedOptions.slice();
            otherSelections[idx] = value;
            var possible = variants.some(function (v) {
              return otherSelections.every(function (val, i) {
                if (val === null) return true;
                return v['option' + (i + 1)] === val;
              }) && v.available;
            });
            btn.disabled = !possible;
          });
        });
      }

      function updateAddState() {
        var variant = findVariant();
        var complete = selectedOptions.every(function (v) { return v !== null; }) || optionGroups.length === 0;
        if (complete && variant && variant.available) {
          addBtn.disabled = isBundleFull();
          if (hint) hint.textContent = addBtn.disabled ? 'Your Edit is complete — remove a piece to add another' : '';
        } else {
          addBtn.disabled = true;
          if (hint) hint.textContent = complete ? 'This combination is sold out' : 'Select options to add this piece';
        }
      }

      optionGroups.forEach(function (group, idx) {
        var buttons = group.querySelectorAll('[data-aussie-edit-option-value]');

        // Colour options render as a dropdown: a closed trigger (preview +
        // label) that expands into a swatch panel on click.
        var swatchDropdown = group.querySelector('[data-aussie-edit-swatch-dropdown]');
        var swatchTrigger = group.querySelector('[data-aussie-edit-swatch-trigger]');
        var swatchPanel = group.querySelector('[data-aussie-edit-swatch-panel]');
        var swatchPreview = group.querySelector('[data-aussie-edit-swatch-preview]');
        var swatchLabel = group.querySelector('[data-aussie-edit-swatch-label]');

        // Every other option (Size, etc.) renders the same dropdown
        // pattern, just with plain text value buttons instead of photos.
        var valueDropdown = group.querySelector('[data-aussie-edit-value-dropdown]');
        var valueTrigger = group.querySelector('[data-aussie-edit-value-trigger]');
        var valuePanel = group.querySelector('[data-aussie-edit-value-panel]');
        var valueLabel = group.querySelector('[data-aussie-edit-value-label]');

        var dropdown = swatchDropdown || valueDropdown;
        var trigger = swatchTrigger || valueTrigger;
        var panel = swatchPanel || valuePanel;

        if (dropdown && trigger && panel) {
          trigger.addEventListener('click', function (e) {
            e.stopPropagation();
            var isOpen = dropdown.classList.contains('is-open');
            closeAllDropdowns();
            if (!isOpen) {
              dropdown.classList.add('is-open');
              panel.hidden = false;
              trigger.setAttribute('aria-expanded', 'true');
            }
          });
          // Prevent a click inside the panel (but not on a value button)
          // from bubbling to the document listener and closing the
          // dropdown before the button's own click handler runs.
          panel.addEventListener('click', function (e) { e.stopPropagation(); });
        }

        buttons.forEach(function (btn) {
          btn.addEventListener('click', function () {
            selectedOptions[idx] = btn.dataset.value;
            buttons.forEach(function (b) { b.classList.remove('is-selected'); });
            btn.classList.add('is-selected');

            // Colour swatches carry the matching variant's own photo —
            // swap the card's main image so the shopper sees the piece
            // in the colour they just picked.
            if (cardImage && btn.dataset.image) {
              cardImage.src = btn.dataset.image;
            }

            // Reflect the pick in whichever dropdown trigger this option
            // uses, then collapse the panel again.
            if (swatchDropdown && swatchPreview && swatchLabel) {
              swatchPreview.style.backgroundImage = btn.style.backgroundImage;
              swatchLabel.textContent = btn.dataset.value;
            }
            if (valueDropdown && valueLabel) {
              valueLabel.textContent = btn.dataset.value;
            }
            if (dropdown && trigger && panel) {
              dropdown.classList.remove('is-open');
              panel.hidden = true;
              trigger.setAttribute('aria-expanded', 'false');
            }

            refreshOptionAvailability();
            updateAddState();
          });
        });
      });

      refreshOptionAvailability();
      updateAddState();

      // Clicking "Add" always ADDS a new entry (up to the bundle size) —
      // it never removes on a second click. This is what lets a shopper
      // pick the very same piece more than once (repeat the same variant,
      // or switch the colour/size dropdown first for a different version)
      // instead of the click toggling it back out. To remove a specific
      // piece already in the Edit, use its own remove button in the
      // drawer (opened via the sticky "REVIEW MY EDIT" button at any time).
      addBtn.addEventListener('click', function () {
        var productId = card.dataset.productId;
        if (isBundleFull()) return;

        var variant = findVariant();
        if (!variant || !variant.available) return;

        var entryId = 'e' + (nextEntryId++);
        selections.push({
          entryId: entryId,
          productId: productId,
          variantId: variant.id,
          compareAt: parseFloat(card.dataset.productCompareAt) * 100 || variant.price,
          title: card.dataset.productTitle,
          optionText: optionSummary(),
          // Snapshot of the card's currently-displayed photo (already
          // swapped to the chosen colour's own image, if applicable) —
          // used as the thumbnail in the drawer's item list.
          image: cardImage ? cardImage.src : ''
        });

        updateCardVisuals(card);
        updateAddState();
        render();

        if (!hasFiredStart) {
          hasFiredStart = true;
          pushEvent('bundle_start', { bundle_name: root.dataset.bundleName || 'Build Your Aussie Style Edit' });
        }
        pushEvent('bundle_product_added', {
          product_id: productId,
          variant_id: variant.id,
          selected_count: selections.length
        });

        // Re-check every other card's add-button state (bundle may now be full)
        cards.forEach(function (c) {
          var evt = new Event('aussie-edit:recheck');
          c.dispatchEvent(evt);
        });
      });

      card.addEventListener('aussie-edit:recheck', updateAddState);
    }

    // How many entries in the current Edit belong to a given product —
    // used to keep a card's own "in your Edit" label/count in sync
    // whenever an entry for that product is added or removed anywhere
    // (card click or drawer remove button).
    function countForProduct(productId) {
      var n = 0;
      selections.forEach(function (s) {
        if (s.productId === productId) n += 1;
      });
      return n;
    }

    // Refreshes a single card's highlighted state (green outline) to
    // reflect whether at least one entry of that product is currently in
    // the Edit. The "ADD TO MY EDIT" button itself is left untouched here
    // on purpose — it always stays green and always says "ADD TO MY
    // EDIT", never switching to a dark "already added" look or wording,
    // so it's obvious the shopper can keep clicking it to add this same
    // piece again (or a different colour/size). The card's own outline is
    // what shows a piece is already in the Edit.
    function updateCardVisuals(card) {
      if (!card) return;
      var productId = card.dataset.productId;
      var count = countForProduct(productId);
      card.classList.toggle('is-selected', count > 0);
    }

    // Removes exactly one entry (by its own entryId) from the Edit,
    // leaving any other entries of the same product untouched.
    function removeEntry(entryId) {
      var idx = -1;
      for (var i = 0; i < selections.length; i++) {
        if (selections[i].entryId === entryId) {
          idx = i;
          break;
        }
      }
      if (idx === -1) return;

      var removed = selections[idx];
      selections.splice(idx, 1);

      var card = cards.find(function (c) { return c.dataset.productId === removed.productId; });
      updateCardVisuals(card);

      render();
      pushEvent('bundle_product_removed', { product_id: removed.productId, selected_count: selections.length });
      cards.forEach(function (c) {
        c.dispatchEvent(new Event('aussie-edit:recheck'));
      });
    }

    function isBundleFull() {
      return selections.length >= bundleSize;
    }

    function currentRetail() {
      var total = 0;
      selections.forEach(function (s) { total += s.compareAt; });
      return total / 100;
    }

    function formatMoney(amount) {
      return '$' + amount.toFixed(2);
    }

    // Rebuilds the drawer's "what you've picked so far" list from the
    // current selections — one row per ENTRY (not per product, so the
    // same piece added twice shows as two separate rows), each with its
    // thumbnail, title (+ chosen colour/size, if any), and its own remove
    // button keyed to that entry alone. Called on every render() so it
    // always reflects the live selection, whether the drawer happens to
    // be open or not.
    function renderDrawerItems() {
      if (!drawerItemsEl) return;
      if (selections.length === 0) {
        drawerItemsEl.innerHTML = '<p class="aussie-edit__drawer-empty">You haven’t picked any pieces yet.</p>';
        return;
      }
      var rows = [];
      selections.forEach(function (s) {
        var thumb = s.image
          ? '<img class="aussie-edit__drawer-item-image" src="' + escapeHtml(s.image) + '" alt="" loading="lazy">'
          : '<span class="aussie-edit__drawer-item-image aussie-edit__drawer-item-image--empty" aria-hidden="true"></span>';
        rows.push(
          '<div class="aussie-edit__drawer-item" data-aussie-edit-drawer-item data-entry-id="' + escapeHtml(s.entryId) + '">' +
            thumb +
            '<span class="aussie-edit__drawer-item-info">' +
              '<span class="aussie-edit__drawer-item-title">' + escapeHtml(s.title) + '</span>' +
              (s.optionText ? '<span class="aussie-edit__drawer-item-option">' + escapeHtml(s.optionText) + '</span>' : '') +
            '</span>' +
            '<button type="button" class="aussie-edit__drawer-item-remove" data-aussie-edit-drawer-item-remove aria-label="Remove ' + escapeHtml(s.title) + ' from your Edit">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M4 7h16M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m2 0v13a1 1 0 01-1 1H8a1 1 0 01-1-1V7h10zM10 11v6M14 11v6"/></svg>' +
            '</button>' +
          '</div>'
        );
      });
      drawerItemsEl.innerHTML = rows.join('');
    }

    function render() {
      var count = selections.length;
      var retail = currentRetail();
      var complete = count >= bundleSize;
      var finalPrice = complete ? fixedPrice : retail;
      var savings = complete ? Math.max(retail - fixedPrice, 0) : 0;

      countEl.textContent = count;
      if (count > 0) {
        summaryEl.hidden = false;
        priceEl.textContent = complete ? formatMoney(fixedPrice) : formatMoney(retail);
        saveEl.textContent = complete ? 'Save ' + formatMoney(savings) : 'Building your Edit…';
      } else {
        summaryEl.hidden = true;
      }

      retailEl.textContent = formatMoney(retail);
      finalEl.textContent = complete ? formatMoney(fixedPrice) : '—';
      savingsEl.textContent = complete ? formatMoney(savings) : '—';

      renderDrawerItems();

      progressBar.classList.toggle('is-complete', complete);
      addToCartBtn.disabled = !complete;
      statusEl.textContent = complete
        ? 'YOUR EDIT IS COMPLETE ✓ — fixed price ' + formatMoney(fixedPrice) + ' unlocked'
        : count + ' of ' + bundleSize + ' selected — pick ' + (bundleSize - count) + ' more';

      if (complete && !hasFiredComplete) {
        hasFiredComplete = true;
        pushEvent('bundle_complete', {
          bundle_name: root.dataset.bundleName || 'Build Your Aussie Style Edit',
          bundle_value: finalPrice,
          savings: savings
        });
        // Open the drawer automatically the instant the Edit becomes complete —
        // no scrolling required to reach the CTA.
        openDrawer();
      }
      if (!complete) hasFiredComplete = false;

      // Sticky bar
      if (count > 0) {
        sticky.hidden = false;
        updateStickyOffset();
        stickyCount.textContent = count + ' of ' + bundleSize + ' selected';
      } else {
        sticky.hidden = true;
      }
    }

    // ---- Filters ----
    // Category pills (e.g. "Dresses") carry a comma-separated list of
    // every collection handle grouped under them — a card matches if it
    // belongs to ANY of those collections, so clicking "Dresses" shows
    // pieces from every dress subcollection combined, with no
    // subcollection picker involved. Visibility matching itself lives in
    // applyActiveFilterVisibility() (shared with the infinite-scroll code
    // above) so newly-appended cards are filtered consistently too.
    filters.forEach(function (btn) {
      btn.addEventListener('click', function () {
        filters.forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        applyActiveFilterVisibility();
        // The newly active category may already have more products
        // waiting server-side — check immediately in case the sentinel
        // is already in view (e.g. a short filtered set on a tall
        // viewport), same as scrolling would trigger.
        loadMoreForActiveFilter();
      });
    });

    // ---- Add bundle to cart ----
    // Adds exactly ONE line item — the fixed-price bundle product/variant.
    // The individually-picked pieces (one per entry, including repeats)
    // are attached as line item properties (visible on the order) so the
    // merchant knows exactly what to fulfil. This never touches the
    // picked pieces' own variants/prices/discounts. On success, sends the
    // shopper straight to Shopify's native checkout (skipping the cart
    // page) — if they're logged into a customer account, checkout
    // automatically uses it (Shopify's own behaviour, nothing extra
    // needed here).
    function addBundleToCart() {
      if (selections.length < bundleSize) return;
      if (!bundleVariantId) {
        console.error('[Aussie Edit] Missing bundle product variant id — configure it in section settings.');
        return;
      }
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = 'ADDING...';

      var properties = {};
      var i = 0;
      var pieceList = [];
      selections.forEach(function (s) {
        i += 1;
        var label = s.optionText ? (s.title + ' — ' + s.optionText) : s.title;
        properties['Piece ' + i] = label;
        pieceList.push(label);
      });

      var retail = currentRetail();
      var savings = Math.max(retail - fixedPrice, 0);

      pushEvent('bundle_add_to_cart', {
        bundle_name: root.dataset.bundleName || 'Build Your Aussie Style Edit',
        pieces: pieceList,
        bundle_value: fixedPrice,
        retail_value: retail,
        savings: savings
      });

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: bundleVariantId, quantity: 1, properties: properties }]
        })
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (data) { throw new Error(data && data.description ? data.description : 'Cart add failed (HTTP ' + res.status + ')'); });
          return res.json();
        })
        .then(function () {
          pushEvent('bundle_checkout', {
            bundle_name: root.dataset.bundleName || 'Build Your Aussie Style Edit',
            bundle_value: fixedPrice
          });
          // Straight to Shopify's native checkout — not the cart page.
          window.location.href = '/checkout';
        })
        .catch(function (err) {
          console.error('[Aussie Edit] Could not add bundle to cart', err);
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = 'ADD BUNDLE TO CART — TRY AGAIN';
          // Surface the real reason on-page (not just in the console) so we
          // can see exactly why it failed on a device we can't inspect
          // directly — e.g. a flaky mobile connection vs. a real cart error.
          if (statusEl) {
            var reason = (err && err.message) ? err.message : 'Network error — check your connection and try again.';
            statusEl.textContent = 'Could not add to cart: ' + reason;
          }
        });
    }

    addToCartBtn.addEventListener('click', addBundleToCart);
    stickyCta.addEventListener('click', openDrawer);

    render();
  }

  function pushEvent(name, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, payload));
  }
})();
